import { ImagePreview } from "@opencode-ai/ui/image-preview"
import { FileIcon } from "@opencode-ai/ui/file-icon"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { ProviderIcon } from "@opencode-ai/ui/provider-icon"
import { ButtonV2 } from "@opencode-ai/ui/v2/button-v2"
import { DialogBody, DialogHeader, DialogTitle, DialogV2 } from "@opencode-ai/ui/v2/dialog-v2"
import { Icon } from "@opencode-ai/ui/v2/icon"
import { KeybindV2 } from "@opencode-ai/ui/v2/keybind-v2"
import { MenuV2 } from "@opencode-ai/ui/v2/menu-v2"
import { TooltipV2 } from "@opencode-ai/ui/v2/tooltip-v2"
import type { ReferenceInfo } from "@opencode-ai/sdk/v2/client"
import { createEffect, createMemo, createResource, For, on, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { ModelSelectorPopoverV2 } from "@/components/dialog-select-model"
import { DialogSelectModelUnpaidV2 } from "@/components/dialog-select-model-unpaid-v2"
import {
  datoxAgentMode,
  downloadArtifact,
  getFile,
  getSessionContextByRemote,
  importIntegrationFiles,
  listArtifactsForPeriod,
  listFiles,
  listIntegrationConnections,
  listIntegrationFiles,
  uploadFile,
  type DatoxCloudFile,
  type DatoxIntegrationConnection,
} from "@/datox/api"
import { workflowChatContext } from "@/datox/workflow-chat-context"
import {
  dashboardWidgetSuggestions,
  transformDashboardWidgetMentions,
  type DashboardWidgetComposerContext,
} from "@/datox/dashboard-widget-context"
import { getAutomationContext } from "@/datox/automations"
import type { PromptInputProps } from "@/components/prompt-input/contracts"
import { normalizePromptHistoryEntry, promptLength, type PromptHistoryComment } from "@/components/prompt-input/history"
import { createPersistedPromptInputHistory } from "@/components/prompt-input/history-store"
import { promptDesignPlaceholder, promptPlaceholder } from "@/components/prompt-input/placeholder"
import { createPromptSubmit } from "@/components/prompt-input/submit"
import { selectionFromLines, type SelectedLineRange, useFile } from "@/context/file"
import { useComments } from "@/context/comments"
import { useCommand } from "@/context/command"
import { useLanguage } from "@/context/language"
import { useLayout } from "@/context/layout"
import { usePermission } from "@/context/permission"
import { type ImageAttachmentPart, type Prompt, usePrompt } from "@/context/prompt"
import { usePlatform } from "@/context/platform"
import { useSDK } from "@/context/sdk"
import { useSync } from "@/context/sync"
import { createSessionTabs } from "@/pages/session/helpers"
import { showToast } from "@/utils/toast"
import { PromptInputV2, type PromptInputV2Suggestion } from "@opencode-ai/session-ui/v2/prompt-input"
import {
  createPromptInputV2Controller,
  createPromptInputV2State,
  type PromptInputV2Interaction,
} from "@opencode-ai/session-ui/v2/prompt-input/interaction"

const datoxAutomateAgentName = "__datox_automate__"
const datoxAnalyticsAgentName = "__datox_analytics__"
const datoxDashboardAgentName = "__datox_dashboard__"

export type PromptInputV2ComposerProps = {
  class?: string
  controller: PromptInputV2ComposerController
  borderUnderlay?: boolean
}

export type PromptInputV2ControllerProps = Omit<PromptInputProps, "class" | "submission"> & {
  dashboardContext?: () => DashboardWidgetComposerContext | undefined
}
export type PromptInputV2ComposerController = PromptInputV2Interaction & {
  readonly model: PromptInputProps["controls"]["model"]
  readonly datoxUploads: Array<{ id: string; name: string; status: "uploading" | "uploaded" | "failed" }>
  importDatoxCloudFiles(providerKey: string, connectionID: string, files: DatoxCloudFile[]): Promise<void>
}

export function PromptInputV2Composer(props: PromptInputV2ComposerProps) {
  const dialog = useDialog()
  const command = useCommand()
  const language = useLanguage()

  return (
    <div class="flex flex-col gap-3">
      <Show when={datoxAgentMode() && props.controller.datoxUploads.length > 0}>
        <div class="flex flex-wrap gap-2 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-2">
          <div class="w-full text-12-medium text-v2-text-text-muted">{language.t("datox.files.panel.title")}</div>
          {props.controller.datoxUploads.map((file) => (
            <div class="flex items-center gap-2 rounded-md bg-v2-background-bg-base px-2 py-1 text-12-regular text-v2-text-text-muted">
              <Icon name="workspace" size="small" />
              <span class="max-w-[220px] truncate">{file.name}</span>
              <span>{language.t(`datox.prompt.upload.${file.status}`)}</span>
            </div>
          ))}
        </div>
      </Show>
      <PromptInputV2
        controller={props.controller}
        borderUnderlay={props.borderUnderlay}
        class={props.class}
        variantControlVisible={!datoxAgentMode() && !props.controller.model.loading}
        attachKeybind={command.keybindParts("file.attach")}
        attachShortcut={command.keybind("file.attach")}
        attachLabel={datoxAgentMode() ? language.t("datox.prompt.uploadFiles") : undefined}
        addMenuActionsVisible={!datoxAgentMode()}
        extraAddMenuActions={
          datoxAgentMode() ? (
            <MenuV2.Item onSelect={() => dialog.show(() => <DialogDatoxIntegrations onImport={props.controller.importDatoxCloudFiles} />)}>{language.t("datox.integrations.menu")}</MenuV2.Item>
          ) : undefined
        }
        modelControl={datoxAgentMode() ? undefined : (
          <PromptInputV2ModelControl
            loading={props.controller.model.loading}
            paid={props.controller.model.paid}
            title={language.t("command.model.choose")}
            keybind={command.keybindParts("model.choose")}
            model={props.controller.model.selection}
            providerID={props.controller.model.selection.current()?.provider?.id}
            modelName={props.controller.model.selection.current()?.name ?? language.t("dialog.model.select.title")}
            onClose={props.controller.restoreFocus}
            onUnpaidClick={() => dialog.show(() => <DialogSelectModelUnpaidV2 model={props.controller.model.selection} />)}
          />
        )}
      />
    </div>
  )
}

const datoxImportProviders = [
  { key: "google-drive", label: "Google Drive", logoPath: "/template-logos/google-drive.svg" },
  { key: "one-drive", label: "OneDrive", logoPath: "/template-logos/one-drive.svg" },
  { key: "sharepoint-online", label: "SharePoint", logoPath: "/template-logos/sharepoint-online.svg" },
] as const

function DialogDatoxIntegrations(props: { onImport: (providerKey: string, connectionID: string, files: DatoxCloudFile[]) => Promise<void> }) {
  const language = useLanguage()
  const dialog = useDialog()
  const [store, setStore] = createStore({ providerKey: "", connectionID: "", search: "", folderStack: [] as Array<{ id: string; name: string }>, selected: {} as Record<string, DatoxCloudFile | undefined>, importing: false })
  const [connections] = createResource(() => listIntegrationConnections())
  const availableProviders = createMemo(() =>
    datoxImportProviders.filter((provider) => (connections()?.connections ?? []).some((connection) => integrationConnectionProviderKey(connection) === provider.key)),
  )
  const activeProvider = createMemo(() => store.providerKey || availableProviders()[0]?.key || "")
  const activeConnection = createMemo(() => (connections()?.connections ?? []).find((connection) => integrationConnectionProviderKey(connection) === activeProvider()) as DatoxIntegrationConnection | undefined)
  const activeFolder = createMemo(() => store.folderStack[store.folderStack.length - 1])
  const [cloudFiles] = createResource(
    () => ({ providerKey: activeProvider(), connectionID: activeConnection()?.connection_id, folderID: activeFolder()?.id }),
    (input) => input.connectionID ? listIntegrationFiles({ providerKey: input.providerKey, connectionID: input.connectionID, folderID: input.folderID }) : Promise.resolve({ items: [], next_cursor: null }),
  )
  const displayedCloudFiles = createMemo(() => {
    const query = store.search.trim().toLowerCase()
    const items = cloudFiles()?.items ?? []
    if (!query) return items
    return items.filter((file) => `${file.name} ${file.mime_type ?? ""}`.toLowerCase().includes(query))
  })
  const selectedFiles = createMemo(() => Object.values(store.selected).filter((file): file is DatoxCloudFile => !!file))
  const importSelected = () => {
    const connection = activeConnection()
    if (!connection || selectedFiles().length === 0) return
    setStore("importing", true)
    void props.onImport(activeProvider(), connection.connection_id, selectedFiles()).then(() => dialog.close()).finally(() => setStore("importing", false))
  }
  return (
    <DialogV2 fit containerClass="!h-auto max-h-[calc(100vh_-_16px)] !w-[min(calc(100vw_-_16px),900px)]">
      <DialogHeader closeLabel={language.t("common.close")}>
        <DialogTitle>{language.t("datox.integrations.title")}</DialogTitle>
      </DialogHeader>
      <DialogBody class="min-h-0 flex-none gap-0 p-4">
        <p class="mb-4 text-13-regular text-v2-text-text-muted">{language.t("datox.integrations.import.description")}</p>
        <Show when={!connections.loading} fallback={<div class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4 text-13-regular text-v2-text-text-muted">{language.t("common.loading")}</div>}>
        <Show when={availableProviders().length > 0} fallback={<div class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 p-6 text-center text-13-regular text-v2-text-text-muted">{language.t("datox.integrations.import.empty")}</div>}>
          <div class="mb-4">
            <div class="mb-2 text-12-medium uppercase text-v2-text-text-muted">{language.t("datox.integrations.import.connectedSources")}</div>
            <div class="grid gap-3 sm:grid-cols-3">
            <For each={availableProviders()}>
              {(provider) => (
                <button type="button" class="flex min-h-[92px] items-start gap-3 rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-3 text-left hover:bg-v2-background-bg-layer-02" classList={{ "border-v2-border-border-muted bg-v2-background-bg-layer-03": activeProvider() === provider.key }} onClick={() => setStore({ providerKey: provider.key, connectionID: "", folderStack: [], selected: {} })}>
                  <span class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white p-2">
                    <img src={provider.logoPath} alt="" class="max-h-full max-w-full" />
                  </span>
                  <span class="min-w-0">
                    <span class="block truncate text-14-medium text-v2-text-text-base">{provider.label}</span>
                    <span class="mt-1 block text-12-regular text-v2-text-text-muted">{language.t("datox.integrations.connected")}</span>
                  </span>
                </button>
              )}
            </For>
            </div>
          </div>
          <div class="mb-2 flex items-center justify-between gap-3">
            <div class="text-12-medium uppercase text-v2-text-text-muted">{language.t("datox.integrations.import.browseFiles")}</div>
            <div class="text-12-regular text-v2-text-text-muted">{language.t("datox.integrations.import.selectedCount", { count: selectedFiles().length })}</div>
          </div>
          <label class="relative mb-4 block max-w-[420px]">
            <input type="search" value={store.search} onInput={(event) => setStore("search", event.currentTarget.value)} placeholder={language.t("common.search.placeholder")} aria-label={language.t("common.search.placeholder")} class="h-9 w-full rounded-full border border-v2-border-border-base bg-v2-background-bg-layer-01 px-4 pr-10 text-14-regular text-v2-text-text-base outline-none placeholder:text-v2-text-text-muted focus:border-v2-border-border-muted" />
            <Icon name="magnifying-glass" size="large" class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-v2-text-text-muted" />
          </label>
          <div class="mb-3 flex flex-wrap items-center gap-2 text-12-medium text-v2-text-text-muted">
            <button type="button" class="rounded-md px-2 py-1 hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base" onClick={() => setStore({ folderStack: [], selected: {} })}>{language.t("datox.integrations.import.root")}</button>
            <For each={store.folderStack}>
              {(folder, index) => (
                <>
                  <span>/</span>
                  <button type="button" class="max-w-[180px] truncate rounded-md px-2 py-1 hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base" onClick={() => setStore({ folderStack: store.folderStack.slice(0, index() + 1), selected: {} })}>{folder.name}</button>
                </>
              )}
            </For>
          </div>
        <div class="h-[390px] overflow-y-scroll rounded-lg border border-v2-border-border-base pr-2 [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]">
            <For each={displayedCloudFiles()} fallback={<div class="py-8 text-center text-13-regular text-v2-text-text-muted">{cloudFiles.loading ? language.t("common.loading") : language.t("datox.integrations.import.noFiles")}</div>}>
              {(file) => (
                <button
                  type="button"
                  class="flex w-full items-center gap-3 border-b border-v2-border-border-base/60 p-3 text-left hover:bg-v2-background-bg-layer-02"
                  onClick={() => file.is_folder ? setStore({ search: "", folderStack: [...store.folderStack, { id: file.id, name: file.name }], selected: {} }) : setStore("selected", file.id, store.selected[file.id] ? undefined : file)}
                >
                  <span class="flex size-9 shrink-0 items-center justify-center rounded-md border border-v2-border-border-base bg-v2-background-bg-base">
                    <Show when={file.is_folder} fallback={<FileIcon node={{ path: file.name, type: "file" }} class="size-4 shrink-0" />}>
                      <Icon name="folder" size="normal" />
                    </Show>
                  </span>
                  <span class="min-w-0 flex-1">
                    <span class="block truncate text-13-medium text-v2-text-text-base">{file.name}</span>
                    <span class="block truncate text-12-regular text-v2-text-text-muted">{file.is_folder ? language.t("datox.integrations.import.folder") : file.mime_type ?? language.t("datox.integrations.import.file")}</span>
                  </span>
                  <Show when={!file.is_folder}>
                    <span class="size-4 rounded border border-v2-border-border-base" classList={{ "bg-v2-text-text-base": !!store.selected[file.id] }} />
                  </Show>
                </button>
              )}
            </For>
        </div>
          <div class="mt-4 flex items-center justify-between border-t border-v2-border-border-base pt-3">
            <span class="text-13-regular text-v2-text-text-muted">{language.t("datox.integrations.import.selectedCount", { count: selectedFiles().length })}</span>
            <ButtonV2 variant="contrast" size="small" disabled={selectedFiles().length === 0 || store.importing} onClick={importSelected}>{store.importing ? language.t("datox.integrations.import.importing") : language.t("datox.integrations.import.selected", { count: selectedFiles().length })}</ButtonV2>
          </div>
        </Show>
        </Show>
      </DialogBody>
    </DialogV2>
  )
}

function integrationConnectionProviderKey(connection: { provider_config_key?: string | null; provider_key?: string | null; provider?: string | null }) {
  return normalizedIntegrationProviderKey(connection.provider_key || connection.provider || connection.provider_config_key)
}

function normalizedIntegrationProviderKey(providerKey: string | null | undefined) {
  const normalized = providerKey?.trim().toLowerCase()
  if (normalized === "sharepoint") return "sharepoint-online"
  if (normalized === "gdrive") return "google-drive"
  if (normalized === "onedrive") return "one-drive"
  return normalized
}

export function usePromptInputV2Controller(props: PromptInputV2ControllerProps): PromptInputV2ComposerController {
  const sdk = useSDK()
  const sync = useSync()
  const files = useFile()
  const layout = useLayout()
  const comments = useComments()
  const dialog = useDialog()
  const command = useCommand()
  const permission = usePermission()
  const language = useLanguage()
  const platform = usePlatform()
  const prompt = props.state ?? usePrompt()
  const [datoxUploads, setDatoxUploads] = createStore<Array<{ id: string; name: string; status: "uploading" | "uploaded" | "failed" }>>([])
  let editor: HTMLDivElement | undefined

  const interaction = createPromptInputV2State()
  const mode = () => interaction[0].mode
  const history = props.history ?? createPersistedPromptInputHistory()
  const tabs = () => props.controls.session.tabs
  const activeFileTab = createSessionTabs({
    tabs,
    pathFromTab: files.pathFromTab,
    normalizeTab: (tab) => (tab.startsWith("file://") ? files.tab(tab) : tab),
  }).activeFileTab
  const recent = createMemo(() => {
    const all = tabs().all()
    const active = activeFileTab()
    const order = active ? [active, ...all.filter((tab) => tab !== active)] : all
    return order.reduce<string[]>((result, tab) => {
      const path = files.pathFromTab(tab)
      if (!path || result.includes(path)) return result
      return [...result, path]
    }, [])
  })
  const info = createMemo(() => (props.controls.session.id ? sync().session.get(props.controls.session.id) : undefined))
  const working = createMemo(() => sync().data.session_working(props.controls.session.id ?? ""))
  const attachments = createMemo(() =>
    prompt.current().filter((part): part is ImageAttachmentPart => part.type === "image"),
  )
  const commentCount = createMemo(() => {
    if (mode() === "shell") return 0
    return prompt.context.items().filter((item) => !!item.comment?.trim()).length
  })
  const blank = createMemo(() => {
    const text = prompt
      .current()
      .map((part) => ("content" in part ? part.content : ""))
      .join("")
    return text.trim().length === 0 && attachments().length === 0 && commentCount() === 0
  })
  const stopping = createMemo(() => working() && blank())
  const placeholder = createMemo(() =>
    promptPlaceholder({
      mode: mode(),
      commentCount: commentCount(),
      example: mode() === "shell" ? "git status" : "",
      suggest: false,
      t: (key, params) => language.t(key as Parameters<typeof language.t>[0], params as never),
    }),
  )
  const designPlaceholder = () =>
    datoxAgentMode()
      ? language.t("datox.prompt.placeholder")
      : promptDesignPlaceholder(mode(), placeholder(), (key, params) =>
          language.t(key as Parameters<typeof language.t>[0], params as never),
        )

  const historyComments = () => {
    const byID = new Map(comments.all().map((item) => [`${item.file}\n${item.id}`, item] as const))
    return prompt.context.items().flatMap((item) => {
      const comment = item.comment?.trim()
      if (!comment) return []
      const selection = item.commentID ? byID.get(`${item.path}\n${item.commentID}`)?.selection : undefined
      const nextSelection =
        selection ??
        (item.selection
          ? ({ start: item.selection.startLine, end: item.selection.endLine } satisfies SelectedLineRange)
          : undefined)
      if (!nextSelection) return []
      return [
        {
          id: item.commentID ?? item.key,
          path: item.path,
          selection: { ...nextSelection },
          comment,
          time: item.commentID ? (byID.get(`${item.path}\n${item.commentID}`)?.time ?? Date.now()) : Date.now(),
          origin: item.commentOrigin,
          preview: item.preview,
        } satisfies PromptHistoryComment,
      ]
    })
  }
  const restoreHistoryComments = (items: PromptHistoryComment[]) => {
    comments.replace(
      items.map((item) => ({
        id: item.id,
        file: item.path,
        selection: { ...item.selection },
        comment: item.comment,
        time: item.time,
      })),
    )
    prompt.context.replaceComments(
      items.map((item) => ({
        type: "file",
        path: item.path,
        selection: selectionFromLines(item.selection),
        comment: item.comment,
        commentID: item.id,
        commentOrigin: item.origin,
        preview: item.preview,
      })),
    )
  }

  const accepting = createMemo(() => {
    const id = props.controls.session.id
    if (!id) return permission.isAutoAcceptingDirectory(sdk().directory)
    return permission.isAutoAccepting(id, sdk().directory)
  })
  const submission = createPromptSubmit({
    prompt,
    info,
    imageAttachments: attachments,
    commentCount,
    autoAccept: accepting,
    mode,
    working,
    editor: () => editor,
    queueScroll: () => requestAnimationFrame(() => editor?.scrollIntoView({ block: "nearest" })),
    promptLength,
    addToHistory: (value, mode) => controller.addHistory(value, mode),
    resetHistoryNavigation: () => controller.resetHistory(),
    setMode: (next) => controller.dispatch({ type: next === "shell" ? "mode.shell" : "mode.normal" }),
    setPopover: (popover) => {
      if (!popover) controller.dispatch({ type: "popover.close" })
    },
    newSessionWorktree: () => props.newSessionWorktree,
    onNewSessionWorktreeReset: props.onNewSessionWorktreeReset,
    shouldQueue: props.shouldQueue,
    onQueue: props.onQueue,
    onAbort: props.onAbort,
    onSubmit: () => {
      setDatoxUploads([])
      props.onSubmit?.()
    },
    transformDraft: injectDatoxContext,
    model: props.controls.model.selection,
  })

  const referenceDescription = (reference: ReferenceInfo) =>
    reference.source.type === "git" ? reference.source.repository : reference.source.path
  const references = createMemo(() =>
    sync()
      .data.reference.filter((reference) => !reference.hidden)
      .map((reference) => ({
        id: `reference:${reference.name}`,
        kind: "reference" as const,
        label: `@${reference.name}`,
        path: reference.path,
        description: reference.description ?? referenceDescription(reference),
        mention: {
          type: "file" as const,
          path: reference.path,
          content: `@${reference.name}`,
          start: 0,
          end: 0,
          mime: "application/x-directory",
          filename: reference.name,
        },
      })),
  )
  const resources = createMemo(() =>
    Object.values(sync().data.mcp_resource).map((resource) => ({
      id: `resource:${resource.server}:${resource.uri}`,
      kind: "resource" as const,
      label: `@${resource.name}`,
      path: resource.uri,
      description: resource.description,
      mention: {
        type: "file" as const,
        path: resource.uri,
        content: `@${resource.name}`,
        start: 0,
        end: 0,
        mime: resource.mimeType ?? "text/plain",
        filename: resource.name,
        url: resource.uri,
        source: {
          type: "resource" as const,
          text: { value: `@${resource.name}`, start: 0, end: resource.name.length + 1 },
          clientName: resource.server,
          uri: resource.uri,
        },
      },
      resource,
    })),
  )
  const dashboardWidgets = createMemo(() => dashboardWidgetSuggestions(props.dashboardContext?.()).map((widget) => ({
    id: widget.id,
    kind: "widget" as const,
    label: widget.label,
    title: widget.label.slice(1),
    description: widget.description,
    mention: { type: "agent" as const, name: widget.mentionName, content: widget.label, start: 0, end: 0 },
  })))
  const context = createMemo<PromptInputV2Suggestion[]>(() => [
    ...references(),
    ...(datoxAgentMode()
      ? [
          {
            id: "datox:automate",
            kind: "agent" as const,
            label: "@automate",
            title: language.t("datox.prompt.automate.title"),
            description: language.t("datox.prompt.automate.description"),
            mention: { type: "agent" as const, name: datoxAutomateAgentName, content: "@automate", start: 0, end: 0 },
          },
          {
            id: "datox:analytics",
            kind: "agent" as const,
            label: "@analytics",
            title: language.t("datox.prompt.analytics.title"),
            description: language.t("datox.prompt.analytics.description"),
            mention: { type: "agent" as const, name: datoxAnalyticsAgentName, content: "@analytics", start: 0, end: 0 },
          },
          {
            id: "datox:dashboard",
            kind: "agent" as const,
            label: "@dashboard",
            title: language.t("datox.prompt.dashboard.title"),
            description: language.t("datox.prompt.dashboard.description"),
            mention: { type: "agent" as const, name: datoxDashboardAgentName, content: "@dashboard", start: 0, end: 0 },
          },
        ]
      : []),
    ...dashboardWidgets(),
    ...props.controls.agents.available
      .filter((agent) => !agent.hidden && agent.mode !== "primary" && !hiddenDatoxAgent(agent.name))
      .map((agent) => ({
        id: `agent:${agent.name}`,
        kind: "agent" as const,
        label: `@${agent.name}`,
        mention: { type: "agent" as const, name: agent.name, content: `@${agent.name}`, start: 0, end: 0 },
      })),
    ...resources(),
    ...recent().map((path) => ({
      id: `file:${path}`,
      kind: "file" as const,
      label: path,
      path,
      recent: true,
      mention: { type: "file" as const, path, content: `@${path}`, start: 0, end: 0 },
    })),
  ])
  const slashCommands = createMemo(() => [
    ...sync().data.command.map((item) => ({
      id: `custom.${item.name}`,
      trigger: item.name,
      title: item.name,
      description: item.description,
      type: "custom" as const,
    })),
    ...command.options
      .filter((item) => !item.disabled && !item.id.startsWith("suggested.") && item.slash)
      .map((item) => ({
        id: item.id,
        trigger: item.slash!,
        title: item.title,
        description: item.description,
        type: "builtin" as const,
      })),
  ])
  const commands = createMemo<PromptInputV2Suggestion[]>(() =>
    slashCommands().map((item) => ({
      id: item.id,
      kind: "command",
      label: `/${item.trigger}`,
      trigger: item.trigger,
      title: item.title,
      description: item.description,
      keybind: command.keybindParts(item.id),
    })),
  )
  const variants = createMemo(() => ["default", ...props.controls.model.selection.variant.list()])
  const controller = createPromptInputV2Controller({
    store: () => prompt.capture().store,
    state: interaction,
    identity: () => prompt.capture(),
    history: {
      entries: (mode) =>
        history.entries(mode).map((value) => {
          const entry = normalizePromptHistoryEntry(value)
          return { prompt: entry.prompt, metadata: entry.comments }
        }),
      add: (value, mode) => history.add(value, mode, mode === "shell" ? [] : historyComments()),
      capture: historyComments,
      restore: (metadata) => restoreHistoryComments(metadata as PromptHistoryComment[]),
    },
    commands,
    context,
    searchContextFiles: async (query) =>
      (await files.searchFilesAndDirectories(query)).map((path) => ({
        id: `file:${path}`,
        kind: "file",
        label: path,
        path,
        mention: { type: "file", path, content: `@${path}`, start: 0, end: 0 },
      })),
    onContextRemove(item) {
      if (item?.commentID) comments.remove(item.path, item.commentID)
    },
    openAttachment: (attachment) =>
      dialog.show(() => <ImagePreview src={attachment.blob.url} alt={attachment.filename} />),
    onAddAttachments: uploadDatoxAttachments,
    openContext(key) {
      const item = controller.contextItem(key)
      if (item) openComment(item, props, sync, layout, files, comments)
    },
    onEditor(element) {
      editor = element as HTMLDivElement
      props.ref?.(editor)
    },
    onSuggestionSelect(item) {
      if (item.kind !== "command") return
      const selected = slashCommands().find((entry) => entry.id === item.id)
      if (!selected || selected.type === "custom") return
      return () => command.trigger(selected.id, "slash")
    },
    attachments: {
      picker: platform.openAttachmentPickerDialog,
      directory: () => sdk().directory,
      isDialogActive: () => !!dialog.active,
      warn: () =>
        showToast({
          title: language.t("prompt.toast.pasteUnsupported.title"),
          description: language.t("prompt.toast.pasteUnsupported.description"),
        }),
      duplicate: () => showToast({ title: language.t("prompt.toast.attachmentDuplicate.title") }),
      onError: (error) =>
        showToast({
          variant: "error",
          title: language.t("common.requestFailed"),
          description: error instanceof Error ? error.message : String(error),
        }),
      readClipboardImage: platform.readClipboardImage,
      getPathForFile: platform.getPathForFile,
      store: platform.draftStore?.putBlob,
    },
    view: {
      placeholder: designPlaceholder,
      get agent() {
        const options = props.controls.agents.options.filter((name) => !hiddenDatoxAgent(name))
        return props.controls.agents.visible && options.length > 0
          ? {
              options: () => options.map((name) => ({ id: name, label: name })),
              current: () => props.controls.agents.current,
              onSelect: (value: string) => props.controls.agents.select(value),
              keybind: () => command.keybindParts("agent.cycle"),
            }
          : undefined
      },
      variant: {
        options: () => variants().map((value) => ({ id: value, label: value })),
        current: () => props.controls.model.selection.variant.current() ?? "default",
        onSelect: (value) => props.controls.model.selection.variant.set(value === "default" ? undefined : value),
        keybind: () => command.keybindParts("model.variant.cycle"),
      },
      submit: {
        stopping,
        working,
        onSubmit: () => void submission.handleSubmit(new Event("submit")),
        onStop: () => void submission.abort(),
      },
    },
  })
  Object.defineProperty(controller, "model", { get: () => props.controls.model })

  command.register("prompt-input", () => [
    {
      id: "file.attach",
      title: language.t("prompt.action.attachFile"),
      category: language.t("command.category.file"),
      keybind: "mod+u",
      disabled: controller.state.mode !== "normal",
      onSelect: () => controller.attach(),
    },
    {
      id: "prompt.mode.shell",
      title: language.t("command.prompt.mode.shell"),
      category: language.t("command.category.session"),
      keybind: "mod+shift+x",
      disabled: controller.state.mode === "shell",
      onSelect: () => controller.dispatch({ type: "mode.shell" }),
    },
    {
      id: "prompt.mode.normal",
      title: language.t("command.prompt.mode.normal"),
      category: language.t("command.category.session"),
      keybind: "mod+shift+e",
      disabled: controller.state.mode === "normal",
      onSelect: () => controller.dispatch({ type: "mode.normal" }),
    },
  ])

  createEffect(
    on(
      () => props.edit?.id,
      (id) => {
        const edit = props.edit
        if (!id || !edit) return
        prompt.context.items().forEach((item) => prompt.context.remove(item.key))
        edit.context.forEach((item) =>
          prompt.context.add({
            type: item.type,
            path: item.path,
            selection: item.selection,
            comment: item.comment,
            commentID: item.commentID,
            commentOrigin: item.commentOrigin,
            preview: item.preview,
          }),
        )
        controller.dispatch({ type: "mode.normal" })
        controller.resetHistory()
        prompt.set(edit.prompt, promptLength(edit.prompt))
        controller.restoreFocus()
        props.onEditLoaded?.()
      },
      { defer: true },
    ),
  )

  return Object.assign(controller, { datoxUploads, importDatoxCloudFiles }) as PromptInputV2ComposerController

  async function injectDatoxContext(draft: Parameters<NonNullable<Parameters<typeof createPromptSubmit>[0]["transformDraft"]>>[0]) {
    if (!datoxAgentMode() || !props.controls.session.id) return draft
    const widgets = transformDashboardWidgetMentions(draft.prompt, props.dashboardContext?.())
    const context = await getSessionContextByRemote(props.controls.session.id)
    if (!context) return { ...draft, prompt: [...widgets.context, ...widgets.prompt] }
    const datoxCommand = datoxCommandPrompt(widgets.prompt)
    const period = context.session_metadata.period as { name?: string } | undefined
    const workflowRunID = crypto.randomUUID()
    const caseID = context.workflow_case?.shared_workflow_records && typeof context.workflow_case.case_id === "string" ? context.workflow_case.case_id : undefined
    const files = await datoxFileManifest(context.project_id, caseID ? context.period_id ?? undefined : undefined, caseID).catch(() => [])
    const currentPeriodFiles = context.period_id
      ? await datoxFileManifest(context.project_id, context.period_id, caseID).catch(() => [])
      : []
    const templates = await datoxTemplateManifest(context.project_id).catch(() => [])
    const automationInstruction = datoxCommand?.command === "automate" && context.period_id
      ? (await getAutomationContext(context.project_id, context.period_id, caseID)).instructions
      : "Automation requires a selected project and period. Ask the user to select them first."
    const prefix = [
      "Datox context for this chat:",
      `project_id: ${context.project_id}`,
      `period_id: ${context.period_id ?? ""}`,
      `period_name: ${period?.name ?? ""}`,
      `datox_session_id: ${context.session_id}`,
      `workflow_run_id: ${workflowRunID}`,
      "project_uploaded_files:",
      JSON.stringify(files),
      "current_period_uploaded_files:",
      JSON.stringify(currentPeriodFiles),
      "report_templates:",
      JSON.stringify(templates),
      "raw_file_attachments: none. Use the project_uploaded_files manifest and file previews supplied in this context; do not expect uploaded files to appear as native chat attachments.",
      "When saving transformations, extractions, output tables, artifacts, or approvals, use the current period_id so the Workflow tabs remain period-specific. You may inspect project-level files, report_templates, and previous-period records as context or templates, especially for automate and analytics requests.",
      "Use the provided workflow_run_id for every save in this user request. Include it as workflow_run_id in extraction, transformation, and report artifact payloads so retries update the same workflow run instead of creating duplicate workflow records.",
      "Save the data categories applied during extraction in result_json.categories: each category has key, name, description and fields (name, type, description, required). Save extracted values separately in result_json.tables. Reuse saved source categories during automation.",
      "When the user asks to generate a report, dashboard, or visual report without @dashboard, always run and persist the full workflow first: save extracted values in Extractions, save transformation steps and output_tables in Transformations, ensure the Output tab has the final report-ready tables, then save the report artifact. Do this even if the user only asks for the report and does not explicitly ask to save extraction or transformation records.",
      "When generating a report, use only the output tables relevant to that report request. Do not mix in unrelated output tables just because they exist in Output history. Save report metadata with `source_output_tables` and `source_transformation_ids` so the UI can show which output tables were used by each report.",
      "Before saving a report artifact, validate that the generated HTML binds non-empty output_tables data into its visible widgets, ECharts options, and tables. Do not save template-only HTML, empty chart arrays, placeholder cards, or reports with blank widgets. Include static visible KPI values/table rows in the HTML markup as fallback; do not rely only on JavaScript to populate all data. If validation fails, fix the HTML/data binding first.",
      ...(datoxCommand ? ["", datoxCommand.command === "automate" ? automationInstruction : datoxCommand.command === "analytics" ? datoxAnalyticsInstruction() : datoxDashboardInstruction()] : []),
      ...(context.workflow_case ? [workflowChatContext(context.workflow_case)] : []),
      "",
    ].join("\n")
    return {
      ...draft,
      prompt: [{ type: "text" as const, content: prefix, start: 0, end: prefix.length, synthetic: true }, ...widgets.context, ...(datoxCommand?.prompt ?? widgets.prompt)],
    }
  }

  function datoxCommandPrompt(prompt: Prompt) {
    let command: "automate" | "analytics" | "dashboard" | undefined
    const next: Prompt = prompt.flatMap((part): Prompt => {
      if (part.type === "agent" && part.name === datoxAutomateAgentName) {
        command = "automate"
        return []
      }
      if (part.type === "agent" && part.name === datoxAnalyticsAgentName) {
        command = "analytics"
        return []
      }
      if (part.type === "agent" && part.name === datoxDashboardAgentName) {
        command = "dashboard"
        return []
      }
      if (part.type !== "text") return [part]
      const content = part.content.replace(/(^|\s)@(automate|automations|analytics|dashboard)\b/gi, (_match, prefix: string, name: string) => {
        command = name.toLowerCase() === "automations" ? "automate" : name.toLowerCase() as "automate" | "analytics" | "dashboard"
        return prefix
      })
      return content === part.content ? [part] : [{ ...part, content, end: part.start + content.length }]
    })
    if (!command) return undefined
    return {
      prompt: next,
      command,
    }
  }

  function datoxDashboardInstruction() {
    return [
      "Dashboard canvas request:",
      "This request explicitly targets the editable Datox Cowork Dashboard tab/canvas. Do not create an HTML report artifact for this request unless the user separately asks for a Report tab artifact or export.",
      "Use list-dashboard-datasets to discover current-period Output tables, list-dashboards/get-dashboard to inspect existing dashboards, and save-dashboard to create or update the editable dashboard draft_config. Use delete-dashboard only when the user explicitly asks to delete a dashboard.",
      "Apply all requested dashboard operations in one dashboard config update when possible, including tabs, widgets, data bindings, filters, sorting, row limits, visible columns, text body, and style changes.",
    ].join("\n")
  }

  function datoxAnalyticsInstruction() {
    return [
      "Analytics request:",
      "Answer using all available project periods by default, not only the current period. Inspect project-level files, previous-period files, extractions, transformations, output tables, reports, and approvals to build historical or cross-period analysis. If the user asks to generate a report or persisted output from this analysis, save the new records under the current period_id unless they explicitly request a project-level or different-period artifact.",
    ].join("\n")
  }

  async function datoxFileManifest(projectID: string, periodID?: string, caseID?: string) {
    const files = await listFiles(projectID, periodID, caseID)
    return Promise.all(
      files.slice(0, 10).map(async (file) => {
        const detail = await getFile(projectID, file.id).catch(() => undefined)
        return {
          file_id: file.id,
          case_id: file.case_id,
          filename: file.filename,
          period_id: file.period_id,
          content_type: file.content_type,
          metadata: file.parsed_metadata,
          parts: (detail?.parts ?? []).slice(0, 25).map((part) => ({
            name: part.name,
            part_type: part.part_type,
            preview_text: (part.preview_text ?? "").slice(0, 2500),
            metadata: part.part_metadata,
          })),
        }
      }),
    )
  }

  async function datoxTemplateManifest(projectID: string) {
    const templates = await listArtifactsForPeriod(projectID, undefined, "report_template")
    return Promise.all(
      templates.slice(0, 3).map(async (template) => {
        const downloaded = await downloadArtifact(projectID, template.id).catch(() => undefined)
        const html = downloaded ? await downloaded.blob.text() : (template.preview_html ?? "")
        return {
          artifact_id: template.id,
          title: template.title,
          format: template.format,
          metadata: template.artifact_metadata,
          html: html.slice(0, 30000),
        }
      }),
    )
  }

  async function uploadDatoxAttachments(files: File[]) {
    if (!datoxAgentMode() || !props.controls.session.id || files.length === 0) return
    const context = await getSessionContextByRemote(props.controls.session.id)
    if (!context) return false
    const entries = files.map((file) => ({ id: crypto.randomUUID(), name: file.name, status: "uploading" as const }))
    setDatoxUploads(entries)
    await Promise.allSettled(
      files.map((file, index) =>
        uploadFile(context.project_id, file, context.period_id ?? undefined, typeof context.workflow_case?.case_id === "string" ? context.workflow_case.case_id : undefined, context.session_id)
          .then((uploaded) => {
            setDatoxUploads(index, "id", uploaded.id)
            setDatoxUploads(index, "status", "uploaded")
          })
          .catch(() => {
            setDatoxUploads(index, "status", "failed")
          }),
      ),
    )
    window.dispatchEvent(new CustomEvent("datox-files-uploaded", { detail: { sessionID: props.controls.session.id } }))
    return []
  }

  async function importDatoxCloudFiles(providerKey: string, connectionID: string, files: DatoxCloudFile[]) {
    if (!datoxAgentMode() || !props.controls.session.id || files.length === 0) return
    const context = await getSessionContextByRemote(props.controls.session.id)
    if (!context) return
    const entries = files.map((file) => ({ id: file.id, name: file.name, status: "uploading" as const }))
    setDatoxUploads(entries)
    await importIntegrationFiles({
      projectID: context.project_id,
      periodID: context.period_id,
      sessionID: context.session_id,
      providerKey,
      connectionID,
      files,
    })
      .then((response) => {
        entries.forEach((entry, index) => {
          setDatoxUploads(index, "id", response.results[index]?.file_id ?? entry.id)
          setDatoxUploads(index, "status", response.results[index]?.status === "imported" ? "uploaded" : "failed")
        })
        window.dispatchEvent(new CustomEvent("datox-files-uploaded", { detail: { sessionID: props.controls.session.id } }))
      })
      .catch(() => entries.forEach((_, index) => setDatoxUploads(index, "status", "failed")))
  }
}

function hiddenDatoxAgent(name: string) {
  if (!datoxAgentMode()) return false
  const normalized = name.toLowerCase()
  return normalized === "plan" || normalized === "build" || normalized === "general" || normalized === "explore"
}

function PromptInputV2ModelControl(props: {
  loading: boolean
  paid: boolean
  title: string
  keybind: string[]
  model: PromptInputV2ComposerController["model"]["selection"]
  providerID?: string
  modelName: string
  onClose: () => void
  onUnpaidClick: () => void
}) {
  const shouldAnimate = createMemo<boolean>((previous) => previous ?? props.loading)
  const content = () => (
    <>
      <Show when={props.providerID}>
        {(providerID) => (
          <ProviderIcon
            id={providerID()}
            class="size-4 shrink-0 opacity-40 group-hover:opacity-100 transition-opacity duration-150"
            style={{ "will-change": "opacity", transform: "translateZ(0)" }}
          />
        )}
      </Show>
      <span class="truncate leading-4">{props.modelName}</span>
      <span class="-ml-0.5 -mr-1 flex shrink-0">
        <Icon name="chevron-down" />
      </span>
    </>
  )
  return (
    <Show when={!props.loading}>
      <TooltipV2
        placement="top"
        gutter={4}
        value={
          <>
            {props.title}
            <KeybindV2 keys={props.keybind} variant="neutral" />
          </>
        }
      >
        <Show
          when={props.paid}
          fallback={
            <ButtonV2
              data-action="prompt-model"
              data-control-type="dialog"
              variant="ghost-muted"
              size="normal"
              class="min-w-0 max-w-[220px] justify-start ![font-weight:440] group"
              classList={{ "animate-in fade-in": shouldAnimate() }}
              style={{ height: "28px" }}
              onClick={props.onUnpaidClick}
            >
              {content()}
            </ButtonV2>
          }
        >
          <ModelSelectorPopoverV2
            model={props.model}
            trigger={(triggerProps) => (
              <ButtonV2
                {...triggerProps}
                variant="ghost-muted"
                size="normal"
                style={{ height: "28px" }}
                class="min-w-0 max-w-[220px] justify-start ![font-weight:440] group"
                classList={{ "animate-in fade-in": shouldAnimate() }}
                data-action="prompt-model"
                data-control-type="popover"
              >
                {content()}
              </ButtonV2>
            )}
            onClose={props.onClose}
          />
        </Show>
      </TooltipV2>
    </Show>
  )
}

function openComment(
  item: { path: string; commentID?: string; commentOrigin?: "review" | "file" },
  props: PromptInputV2ControllerProps,
  sync: ReturnType<typeof useSync>,
  layout: ReturnType<typeof useLayout>,
  files: ReturnType<typeof useFile>,
  comments: ReturnType<typeof useComments>,
) {
  if (!item.commentID) return
  const focus = { file: item.path, id: item.commentID }
  comments.setActive(focus)
  const queueFocus = (attempts = 6) => {
    requestAnimationFrame(() => {
      comments.setFocus({ ...focus })
      if (attempts <= 0) return
      requestAnimationFrame(() => {
        const current = comments.focus()
        if (current?.file === focus.file && current.id === focus.id) queueFocus(attempts - 1)
      })
    })
  }
  const diffs = props.controls.session.id ? sync().data.session_diff[props.controls.session.id] : undefined
  const review =
    item.commentOrigin === "review" || (item.commentOrigin !== "file" && diffs?.some((diff) => diff.file === item.path))
  if (!props.controls.session.reviewPanel.opened()) props.controls.session.reviewPanel.open()
  if (review) {
    layout.fileTree.setTab("changes")
    props.controls.session.tabs.setActive("review")
    queueFocus()
    return
  }
  layout.fileTree.setTab("all")
  const tab = files.tab(item.path)
  void props.controls.session.tabs.open(tab)
  props.controls.session.tabs.setActive(tab)
  void Promise.resolve(files.load(item.path)).finally(() => queueFocus())
}
